import {
  append,
  propEq,
  reject,
  map,
  prop,
  values,
  find,
  uniq,
  path,
  pipe,
  filter,
  evolve,
  isEmpty,
  flatten,
  anyPass
} from 'ramda'
import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import styled from 'styled-components'
import { Button } from '@/components/button'
import CurrencyFormatter from '@/components/currency-formatter'
import { Input } from '@/components/form'
import { createModal, useModalize } from '@/components/modalize/mui'
import useTenantApplications from '@/hooks/use-tenant-applications'
import { groupedListings } from '@/models/listing/selectors'
import useMyListings from '@/pages/Listing/MyListings/hooks/use-my-listings'
import { colors, fontSizes, fontWeights, breakpoints } from '@/styles'
import { getImageUrl } from '@/utils/image'
import toInteger from '@/utils/to-integer'
import { H4 } from '@/components/text'
import defaultImage from './building-default.png'
import { showCopyConfirmationModal } from './copy-confirmation-modal'
import { CloseIcon } from '../icons'

const Wrapper = styled.div`
  display: flex;
  flex-direction: column;
`

const Title = styled(H4)`
  position: sticky;
  top: 0;
  width: 100%;
  padding: 24px 24px 16px;
  background-color: ${colors.white};
  border-bottom: 1px solid ${colors.grey80};
  display: flex;
  justify-content: space-between;
  align-items: center;
  font-size: 16px;
`

const ScrollWrapper = styled.div`
  height: 100%;
  overflow: auto;
`

const CheckBoxBase = styled(Input)`
  flex-shrink: 0;
  margin: ${props => props.margin || 0};
`

const CheckBoxContainer = styled.div`
  margin: 0 20px 0 auto;
`

const CheckBox = props => (
  <CheckBoxContainer>
    <CheckBoxBase {...props} />
  </CheckBoxContainer>
)

const AddWrapper = styled.div`
  width: 100%;
  display: flex;
  justify-content: flex-end;
  padding: 16px;
  flex-shrink: 0;
  position: sticky;
  border-top: 1px solid ${colors.grey80};
  bottom: 0;
  background-color: ${colors.grey96};
`

const BuildingListWrapper = styled.div`
  padding: 0;
  margin: 0;
  display: flex;
  flex-direction: column;
  align-items: stretch;

  :not(:first-child) {
    border-top: 1px solid ${colors.grey80};
  }

  ${props =>
    props.asRow &&
    `
    flex-direction: row;
    justify-content: space-between;
    align-items: center;
  `}
`

const BuildingInfoCardWrapper = styled.div`
  padding: 15px 15px;
  background: ${props => (props.darker ? colors.lightGrey : colors.white)};
`

const BuildingName = styled.p`
  font-weight: ${fontWeights.medium};
  font-size: ${fontSizes.regular};
  padding: 0;
  margin: 0 0 2px;

  @media screen and (max-width: ${breakpoints.phoneMax}) {
    font-size: 13px;
  }
`

const BuildingText = styled.p`
  padding: 0;
  margin: 0 0 2px;
  font-size: 15px;

  :last-child {
    margin: 0;
  }

  @media screen and (max-width: ${breakpoints.phoneMax}) {
    font-size: 12px;
  }
`

const ListingOptionsWrapper = styled.div`
  width: 100%;
  padding: 0 20px 0 15px;
  background-color: #eaeff3;
`

const ListingOption = styled.div`
  margin: 0;
  width: 100%;
  display: flex;
  justify-content: flex-start;
  align-items: center;
  padding: 15px 0 15px 15px;

  :not(:first-child) {
    border-top: 1px solid ${colors.mediumLightGrey};
  }

  @media screen and (max-width: ${breakpoints.phoneMax}) {
    padding-left: 10px;
  }
`

const UnitInfoWrapper = styled.div`
  display: flex;
  align-items: center;
  justify-content: flex-start;
`

const UnitInfoColumn = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  text-align: left;
  justify-content: flex-start;
`

const Unit = styled.p`
  margin: 0 0 0px;
  padding: 0;
  font-weight: ${fontWeights.medium};
  font-size: 14px;

  @media screen and (max-width: ${breakpoints.phoneMax}) {
    font-size: 12px;
  }
`

const UnitPrice = styled.p`
  margin: 0;
  padding: 0;
  font-size: ${fontSizes.small};

  @media screen and (max-width: ${breakpoints.phoneMax}) {
    font-size: 11px;
  }
`

const Applied = styled.p`
  padding: 4px 15px;
  border-radius: 15px;
  background: ${colors.pink};
  color: ${colors.white};
  font-size: ${fontSizes.small};
  margin: ${props => props.margin || '0 0 0 auto'};

  @media screen and (max-width: ${breakpoints.phoneMax}) {
    padding: 3px 12px;
    border-radius: 12px;
    font-size: 12px;
  }
`

const ListingImage = styled.img`
  width: ${props => props.size || 60}px;
  height: ${props => props.size || 60}px;
  margin: ${props => props.margin || `0 15px 0 0`};
`

const StyledCloseIcon = styled(CloseIcon)`
  cursor: pointer;
`

const filterActiveListings = pipe(
  map(
    evolve({
      listings: filter(
        anyPass([propEq('state_machine', 'published'), propEq('state_machine', 'pending')])
      )
    })
  ),
  reject(building => isEmpty(building.listings))
)

const BuildingInfoCard = ({ name, address, city, state, unit, priceText, darker }) => {
  return (
    <BuildingInfoCardWrapper darker={darker}>
      <BuildingName>
        {unit && `${unit} - `}
        {name}
      </BuildingName>
      <BuildingText>{address}</BuildingText>
      {!!city && !!state && (
        <BuildingText>
          {city}, {state}
        </BuildingText>
      )}
      {priceText && <BuildingText>{priceText}</BuildingText>}
    </BuildingInfoCardWrapper>
  )
}

const UnitInfo = ({ unitNumber, price }) => (
  <UnitInfoWrapper>
    <UnitInfoColumn>
      <Unit>Unit {unitNumber}</Unit>
      <UnitPrice>{price}</UnitPrice>
    </UnitInfoColumn>
  </UnitInfoWrapper>
)

const BuildingListingsList = ({
  listings = [],
  changeSelectedListing,
  selectedListing,
  listingsWithApplication = [],
  ...buildingData
}) => {
  const { t } = useTranslation()
  const showListingOptions = listings.length > 1
  const getPriceText = listing => {
    return toInteger(listing.gr_unit) === 0 ? (
      <CurrencyFormatter value={listing.price} />
    ) : (
      <>
        <CurrencyFormatter value={listing.gr_min_price} /> -{' '}
        <CurrencyFormatter value={listing.gr_max_price} />
      </>
    )
  }

  const alreadyApplied = listingId => listingsWithApplication.indexOf(listingId) !== -1

  const appliedLabel = t('l.appliction.copy_modal.applied.label', 'Applied')

  const getListingFileId = listing => {
    return !!listing.listing_files ? Object?.keys(listing.listing_files)[0] : null
  }

  return (
    <BuildingListWrapper
      asRow={!showListingOptions}
      as={!showListingOptions && !alreadyApplied(listings[0].listing_id) ? 'label' : 'div'}
    >
      {!showListingOptions && (
        <ListingImage
          size={90}
          margin="0 0 0 15px"
          src={
            getImageUrl(
              path([getListingFileId(listings[0]), 'aws_s3_key'], listings[0].listing_files),
              {
                width: 90,
                height: 90
              }
            ) || defaultImage
          }
        />
      )}
      <BuildingInfoCard
        name={buildingData.building_name}
        address={buildingData.address}
        city={buildingData.city}
        state={buildingData.state}
        priceText={!showListingOptions && getPriceText(listings[0])}
        darker={showListingOptions}
        unit={!showListingOptions && listings[0].unit_number}
        imageSrc={getImageUrl(
          path([getListingFileId(listings[0]), 'aws_s3_key'], listings[0].listing_files),
          {
            width: 90,
            height: 90
          }
        )}
      />
      {showListingOptions ? (
        <ListingOptionsWrapper>
          {map(
            listing => (
              <ListingOption
                key={listing.listing_id}
                as={alreadyApplied(listing.listing_id) ? 'div' : 'label'}
              >
                <ListingImage
                  src={
                    getImageUrl(
                      path([getListingFileId(listing), 'aws_s3_key'], listing.listing_files),
                      {
                        width: 90,
                        height: 90
                      }
                    ) || defaultImage
                  }
                />
                <UnitInfo unitNumber={listing.unit_number} price={getPriceText(listing)} />
                {alreadyApplied(listing.listing_id) ? (
                  <Applied>{appliedLabel}</Applied>
                ) : (
                  <CheckBox
                    onClick={() => changeSelectedListing(listing.listing_id)}
                    checked={selectedListing === listing.listing_id}
                    round
                    type="radio"
                    margin={'0 0 0 auto'}
                    readOnly
                  />
                )}
              </ListingOption>
            ),
            listings
          )}
        </ListingOptionsWrapper>
      ) : !alreadyApplied(listings[0].listing_id) ? (
        <CheckBox
          onClick={() => changeSelectedListing(listings[0].listing_id)}
          checked={selectedListing === listings[0].listing_id}
          round
          type="radio"
          margin={'0 20px 0 auto'}
          readOnly
        />
      ) : (
        <Applied margin={'0 20px 0 auto'}>{appliedLabel}</Applied>
      )}
    </BuildingListWrapper>
  )
}

const CopyApplicationModal = ({ application, history, listingId }) => {
  const { close } = useModalize()
  const { t } = useTranslation()
  const [selectedListing, setSelectedListing] = useState(null)
  const { listings: allMyListings } = useMyListings()
  const activeListings = filterActiveListings(allMyListings || [])
  const tenantApplications = useTenantApplications(application?.user_id)
  const buildings = groupedListings(activeListings || [])
  const listingsWithApplication = pipe(
    prop('applications'),
    values,
    map(prop('listing_id')),
    append(application.listing_id),
    uniq
  )(tenantApplications)

  const title = t('l.application.copy_modal.title.text', 'Copy Applicant To...')

  const onChangeSelectedListing = listingId => {
    setSelectedListing(listingId === selectedListing ? null : listingId)
  }

  const getBuildingListings = building => building.listings

  const allListings = flatten(map(getBuildingListings, buildings))

  const selectedListingObject = find(propEq('listing_id', selectedListing))(allListings)

  const applicationName =
    application.user_info !== undefined
      ? `${application.user_info.preferred_name} ${application.user_info.last_name}`
      : `${application.preferred_name} ${application.last_name}`

  return (
    <Wrapper>
      <Title>
        {title} <StyledCloseIcon onClick={close} width={'15px'} height={'15px'} />
      </Title>
      <ScrollWrapper>
        {map(
          building => (
            <BuildingListingsList
              {...building}
              key={building.building_id}
              selectedListing={selectedListing}
              changeSelectedListing={onChangeSelectedListing}
              listingsWithApplication={listingsWithApplication}
            />
          ),
          buildings
        )}
      </ScrollWrapper>
      <AddWrapper>
        <Button
          theme="primary"
          onClick={() =>
            showCopyConfirmationModal({
              name: `${applicationName}`,
              address: selectedListingObject?.address,
              applicationId: application.id,
              targetListingId: selectedListing,
              currentListingId: listingId,
              closeCopyModal: close,
              history
            })
          }
          disabled={!selectedListing}
        >
          {t('common.submit', 'Submit')}
        </Button>
      </AddWrapper>
    </Wrapper>
  )
}

export const showCopyApplicationModal = createModal(CopyApplicationModal, {
  width: '500px',
  showClose: false
})
